import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import '../../assets/css/post.css';
import SkeletonPost from '../../shared/components/skeletonpost';

const Post = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch do post
  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Busca o post específico
        const response = await fetch(
          `https://wordpress.zauso-dev.com.br/wp-json/wp/v2/posts?slug=${slug}&_embed`
        );
        
        if (!response.ok) throw new Error('Post não encontrado');
        
        const data = await response.json();
        
        if (data.length === 0) {
          throw new Error('Post não encontrado');
        }
        
        setPost(data[0]);
        
        // Busca posts relacionados (mesma categoria)
        if (data[0].categories && data[0].categories.length > 0) {
          fetchRelatedPosts(data[0].categories[0]);
        }
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPost();
  }, [slug]);

  // Fetch posts relacionados
  const fetchRelatedPosts = async (categoryId) => {
    try {
      const response = await fetch(
        `https://wordpress.zauso-dev.com.br/wp-json/wp/v2/posts?categories=${categoryId}&per_page=3&exclude=${post?.id}`
      );
      const data = await response.json();
      setRelatedPosts(data);
    } catch (error) {
      console.error('Erro ao buscar posts relacionados:', error);
    }
  };

  // Formata data
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtém imagem destacada
  const getFeaturedImage = () => {
    return post?._embedded?.['wp:featuredmedia']?.[0];
  };

  // Obtém categorias
  const getCategories = () => {
    return post?._embedded?.['wp:term']?.[0] || [];
  };

  // Obtém autor
  const getAuthor = () => {
    return post?._embedded?.author?.[0];
  };

  // Loading state
  if (loading) {
    return (
     <SkeletonPost cardsCount={1} />
    );
  }

  // Error state
  if (error || !post) {
    return (
      <div className="post-error">
        <h2>Artigo não encontrado</h2>
        <p>{error || 'O artigo que você está procurando não existe.'}</p>
        <button 
          onClick={() => navigate('/blog')}
          className="back-to-blog-btn"
        >
          ← Voltar para o Blog
        </button>
      </div>
    );
  }

  const featuredImage = getFeaturedImage();
  const categories = getCategories();
  const author = getAuthor();

  return (
    <div className="post-container">
      {/* Breadcrumb */}
      <nav className="breadcrumb">
        <Link to="/">Home</Link>
        <span> / </span>
        <Link to="/blog">Blog</Link>
        <span> / </span>
        <span className="current">{post.title.rendered}</span>
      </nav>

      {/* Conteúdo Principal */}
      <article className="post-content">
        {/* Header do Post */}
        <header className="post-header">
          {/* Categorias */}
          {categories.length > 0 && (
            <div className="post-categories">
              {categories.map(cat => (
                <Link 
                  key={cat.id} 
                  to={`/blog?category=${cat.id}`}
                  className="post-category"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          )}

          {/* Título */}
          <h1 
            className="post-title"
            dangerouslySetInnerHTML={{ __html: post.title.rendered }}
          />

          {/* Meta informações */}
          <div className="post-meta">
            {/* Autor */}
            {author && (
              <div className="post-author">
                <div className="author-avatar">
                  {author.avatar_urls && (
                    <img 
                      src={author.avatar_urls[48]} 
                      alt={author.name}
                      className="avatar"
                    />
                  )}
                </div>
                <div className="author-info">
                  <span className="author-name">{author.name}</span>
                  {author.description && (
                    <span className="author-bio">{author.description}</span>
                  )}
                </div>
              </div>
            )}

            {/* Data */}
            <div className="post-date-info">
              <span className="publish-date">
                   <em>Publicado em: {formatDate(post.date)}</em> 
              </span>
              {post.modified !== post.date && (
                <span className="update-date">
                   <em>Atualizado em: {formatDate(post.modified)}</em>
                </span>
              )}
            </div>
          </div>

          {/* Imagem Destacada */}
          {featuredImage && (
            <div className="featured-image-container">
              <img
                src={featuredImage.source_url}
                alt={featuredImage.alt_text || post.title.rendered}
                className="featured-image"
              />
              {featuredImage.caption && (
                <figcaption 
                  className="image-caption"
                  dangerouslySetInnerHTML={{ __html: featuredImage.caption.rendered }}
                />
              )}
            </div>
          )}
        </header>

        {/* Conteúdo do Post */}
        <div className="post-body">
          {/* Sidebar de navegação (para posts longos) */}
          <aside className="post-sidebar">
            <div className="table-of-contents">
              <h3>📖 Neste artigo</h3>
              <ul>
                {/* Isso pode ser gerado dinamicamente do conteúdo */}
                <li><a href="#introducao">Introdução</a></li>
                <li><a href="#desenvolvimento">Desenvolvimento</a></li>
                <li><a href="#conclusao">Conclusão</a></li>
              </ul>
            </div>
            
            {/* <div className="share-widget">
              <h3>📤 Compartilhar</h3>
              <div className="share-buttons">
                <button className="share-btn facebook">Facebook</button>
                <button className="share-btn twitter">Twitter</button>
                <button className="share-btn linkedin">LinkedIn</button>
                <button className="share-btn copy">Copiar Link</button>
              </div>
            </div> */}
          </aside>

          {/* Conteúdo principal com estilos WordPress */}
          <div className="post-main-content">
            <div 
              className="wp-content"
              dangerouslySetInnerHTML={{ __html: post.content.rendered }}
            />

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="post-tags">
                <span className="tags-label">🏷️ Tags:</span>
                <div className="tags-list">
                  {post.tags.map(tagId => {
                    // Você precisaria buscar as tags separadamente
                    return (
                      <span key={tagId} className="tag">
                        Tag {tagId}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Autor Box */}
            {author && (
              <div className="author-box">
                <div className="author-box-header">
                  <h3>Sobre o autor</h3>
                </div>
                <div className="author-box-content">
                  {author.avatar_urls && (
                    <img 
                      src={author.avatar_urls[96]} 
                      alt={author.name}
                      className="author-box-avatar"
                    />
                  )}
                  <div className="author-box-info">
                    <h4>{author.name}</h4>
                    {author.description && (
                      <p>{author.description}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navegação entre posts */}
        {/* <div className="post-navigation">
          <button className="nav-btn prev">
            ← Artigo Anterior
          </button>
          <Link to="/blog" className="nav-btn all">
            Todos os Artigos
          </Link>
          <button className="nav-btn next">
            Próximo Artigo →
          </button>
        </div> */}
      </article>

      {/* Posts Relacionados */}
      {relatedPosts.length > 0 && (
        <section className="related-posts">
          <h2>📚 Artigos Relacionados</h2>
          <div className="related-grid">
            {relatedPosts.map(relatedPost => {
              const relatedImage = relatedPost._embedded?.['wp:featuredmedia']?.[0];
              
              return (
                <article 
                  key={relatedPost.id}
                  className="related-card"
                  onClick={() => navigate(`/blog/${relatedPost.slug}`)}
                >
                  {relatedImage && (
                    <img
                      src={relatedImage.source_url}
                      alt={relatedImage.alt_text || relatedPost.title.rendered}
                      className="related-image"
                    />
                  )}
                  <h3 
                    className="related-title"
                    dangerouslySetInnerHTML={{ __html: relatedPost.title.rendered }}
                  />
                  <div 
                    className="related-excerpt"
                    dangerouslySetInnerHTML={{ 
                      __html: relatedPost.excerpt.rendered.replace(/<[^>]+>/g, '').substring(0, 100) + '...' 
                    }}
                  />
                </article>
              );
            })}
          </div>
        </section>
      )}

      {/* Comentários (futura implementação) */}
      <section className="comments-section">
        <h2>💬 Comentários</h2>
        <div className="comments-placeholder">
          <p>Os comentários estão desabilitados no momento.</p>
          <p>Entre em contato pelas redes sociais para conversarmos sobre este artigo!</p>
        </div>
      </section>
    </div>
  );
};

export default Post;